/**
 * Frontend-Only Types
 *
 * Types used exclusively by the frontend UI layer.
 * These types DO NOT exist in the backend and should NOT be sent to backend APIs.
 *
 * Use cases:
 * - Component props and UI state
 * - OAuth/JWT user information
 * - UI helpers and selections
 * - Form configurations
 */

import type { Customer, AwsAccount, AwsAccountScopeItem, RuleDeployment, OpsItemStrategyType, RuleTypeType, AutomationContentTypeType, ItsmChannel, NotificationTarget, AutomationChannelRequest, ResolveSignal, PhaseOutcomeType, VerifyOutcomeType } from './backend';

// ============================================================================
// Escalation Chain Types
// ============================================================================

export interface ChannelIntegration {
  channel_id: string;
  channel_type: 'manual' | 'automated';
  label: string;
  config: Record<string, unknown>;
}

export interface ChannelStep {
  channel_id: string;
  contact_username: string | null;
  description: string;
}

export interface EscalationLevel {
  level_number: number;
  level_type: 'cloud2' | 'customer';
  name: string;
  description: string;
  contact_username: string | null;
  channel_steps: ChannelStep[];
}

export interface EscalationChain {
  customer_id: string;
  levels: EscalationLevel[];
  available_channels: ChannelIntegration[];
  updated_at: string | null;
  updated_by: string | null;
}

// ============================================================================
// Contact Management Types (UI Helpers)
// ============================================================================

/**
 * Enriched contact returned by ListContacts.
 * Joins Cognito user identity with DynamoDB contact metadata.
 */
export interface ContactWithUser {
  username: string;
  email: string;
  name: string;
  phone_number?: string;
  status: string;
  enabled: boolean;
  contact_types: string[];
  operational_priority: number | null;
  role: string;
}

// ============================================================================
// Authentication & User Types
// ============================================================================

/**
 * User context from Cognito User Pool.
 * Provides user permissions and customer access control.
 */
export interface UserContext {
  user_id: string;
  email: string;
  name: string;
  avatar_url?: string;

  // Customer access (from Cognito groups)
  customer_groups: string[]; // List of customer IDs user has access to
  is_admin: boolean;          // Can configure AWS accounts for any customer

  // Session info
  sso_session_token?: string;
  expires_at?: string;
}

// ============================================================================
// AWS Account Management Types (UI Helpers)
// ============================================================================

/**
 * AWS account configuration for create/update forms.
 * Frontend helper for constructing API requests.
 */
export interface AWSAccountConfig {
  customer_id: string;
  /** Immutable: an update accepts only the number the account already has. */
  aws_account_id: string;
  account_name: string;
  read_role_name: string;
  write_role_name?: string;
  /** Refused by create and update: set on customers.SetAWSAccountAdminRoles. */
  automation_role_name?: never;
  /** Refused by create and update: set on customers.SetAWSAccountAdminRoles. */
  onboarding_role_name?: never;
  external_id?: string;
  main_region: string;
  supported_regions: string[];
  event_bus_name?: string;
  /** Update only: CreateAWSAccount refuses it, since an account is created enabled. */
  enabled?: boolean;
  /** Create only: split out of the registration and sent on its own admin action. */
  break_glass_required?: boolean;
}

/**
 * Request to test AWS role access.
 * Used by AwsAccountRoleTester component.
 */
export interface TestAccessRequest {
  role_name: string;
  external_id?: string;
}

/**
 * Response from AWS role access test.
 */
export interface TestAccessResponse {
  success: boolean;
  message?: string;
  error?: string;
}

// ============================================================================
// Investigation Scope Types (UI Helpers)
// ============================================================================

/**
 * Investigation scope configuration for update requests.
 * Frontend helper for scope modal.
 *
 * @deprecated Use InvestigationScope from backend.ts instead (with accounts array)
 */
export interface InvestigationScopeConfig {
  accounts: AwsAccountScopeItem[];
}

/**
 * AWS account selection state for scope selection UI.
 * Used by InvestigationScopeModal for multi-select checkboxes.
 */
export interface AwsAccountSelection {
  account: AwsAccount;
  selected: boolean;
}

// ============================================================================
// Customer Management Types (UI Helpers)
// ============================================================================

/**
 * Customer with associated AWS accounts.
 * Composite view for customer management UI.
 */
export interface CustomerWithAccounts {
  customer: Customer;
  accounts: AwsAccount[];
  accessible: boolean; // Based on user's customer_groups
}

/**
 * Operations account in a CustomerAccountsResponse.
 */
export interface OperationsAccountInfo {
  account_id: string;
  account_type: 'operations';
  region: string;
  role: string;
}

/**
 * Structured response from customers.ListAWSAccounts.
 * customer_accounts contains the full AwsAccount model_dump with an added account_type field.
 */
export interface CustomerAccountsResponse {
  operations_account: OperationsAccountInfo;
  customer_accounts: (AwsAccount & { account_type: 'customer' })[];
  total_accounts: number;
}

// ============================================================================
// Component UI Types
// ============================================================================

/**
 * WebSocket connection state.
 * Used for connection status indicator.
 */
export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

export interface ApiErrorResponse {
  response?: {
    data?: {
      error?: string;
      detail?: string;
    };
    status?: number;
  };
  message?: string;
}

// ============================================================================
// Action Log Types (from OpsItems cross-account query)
// ============================================================================

export interface ActionLogEntry {
  opsitem_id: string;
  title: string;
  source?: string;
  detail_type?: string;
  status: string;
  severity: string;
  rule_name?: string;
  rule_type?: string;
  created_at: string;
  updated_at?: string;
  automation_result?: {
    result_summary?: string;
    content_type?: AutomationContentTypeType;
    run_id?: string;
    status?: string;
    completed_at?: string;
    [key: string]: unknown;
  };
  /** s3:// pointer to the rendered report HTML; present only on report OpsItems. */
  report_ref?: string;
}

export interface ActionLogResponse {
  items: ActionLogEntry[];
  next_token?: string;
}

// ============================================================================
// Event Rule Testing Types (Temporary - pending backend implementation)
// ============================================================================

/**
 * Request to test field mapping transformations.
 * Used by event rule transformation testing.
 */
export interface TransformTestRequest {
  field_mappings: Record<string, string>;
  test_event: Record<string, unknown>;
}

/**
 * Response from field mapping transformation test.
 */
export interface TransformTestResponse {
  success: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  extracted_fields: Record<string, any>;
  errors: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  transformed_output: any;
}

// ============================================================================
// Event Management Types (Phase 4-5: Mock until backend implementation)
// ============================================================================

/**
 * Trigger type for rules - event-based or schedule-based
 */
export type TriggerType = 'event' | 'schedule';

/**
 * Schedule preset options for schedule-based rules
 */
export type SchedulePreset = 'hourly' | 'daily' | 'weekly' | 'monthly';

// LiveEvent, EventFacets, and EventsResponse are exported from backend.ts
// Re-export for backward compatibility
export type { LiveEvent, EventFacets, EventsResponse } from './backend';

/** The event a dataset row came from, to open in the Event Explorer (EM-UC-37, 6a). */
export interface RowEventTarget {
  id: string;
  /** The event time, ISO 8601 UTC. */
  time: string;
}

/**
 * Filters for querying recent events (frontend query params)
 */
export interface EventFilters {
  /** Time range to query (e.g., '15m', '1h', '6h', '24h') */
  timeRange?: string;
  /** Custom range start (ISO 8601) — when set with endTime, overrides timeRange */
  startTime?: string;
  /** Custom range end (ISO 8601) — when set with startTime, overrides timeRange */
  endTime?: string;
  /** Filter by event source */
  source?: string;
  /** Filter by detail type */
  detailType?: string;
  /** Filter by region */
  region?: string;
  /** Filter by AWS account ID */
  account?: string;
  /** Get events since this ISO timestamp (for polling) */
  since?: string;
  /** Max events to return */
  limit?: number;
  /** Exclude events from these sources (comma-separated) */
  excludeSources?: string;
  /** Exclude events of these detail types (comma-separated) */
  excludeDetailTypes?: string;
  /** Exclude events from these regions (comma-separated) */
  excludeRegions?: string;
  /** Exclude events from these accounts (comma-separated) */
  excludeAccounts?: string;
  /** Full-text search term — matched server-side across the raw event message */
  search?: string;
}

/**
 * @deprecated Use EventsResponse from backend.ts instead
 */
export interface LiveEventsResponse {
  /** List of events matching filters */
  events: import('./backend').LiveEvent[];
  /** Total count (may be more than returned) */
  total: number;
  /** Whether more events are available */
  hasMore: boolean;
}

/**
 * Rule template owner type.
 *
 * The backend `TemplateResponse`/`TemplateListItem` model `owner` as a plain
 * `string` (never narrowed) — this union is an unverified FE narrowing kept
 * because the backend `TemplateOwner` enum only ever produces these two
 * values in practice.
 */
export type TemplateOwnerType = 'global' | 'customer';

/**
 * Rule template for quick rule creation (backend-persisted).
 *
 * Matches the backend `TemplateResponse` model (events.GetTemplate /
 * events.UpdateTemplate) — NOT the rich per-channel rule shape. The backend
 * stores itsm/automation/notification config as opaque blobs
 * (`itsm_config`, `automation_prompt_id`, `notifications`) rather than the
 * typed `ItsmChannel`/`AutomationChannel`/`NotificationTarget[]` shapes rules
 * use — there is no schema enforcing they match. Conversion to/from the
 * typed rule-form shapes happens in ruleFormUtils.ts / TemplateEditor.tsx.
 */
export interface RuleTemplate {
  template_id: string;
  owner: TemplateOwnerType;
  customer_id?: string;
  name: string;
  description: string;
  category: string;
  tags: string[];
  rule_type: string;
  trigger_type: string;
  event_pattern?: Record<string, unknown>;
  schedule_expression?: string;
  itsm_config?: Record<string, unknown>;
  enrichment_prompt_id?: string;
  automation_prompt_id?: string;
  /** The batch agent that runs automation_prompt_id; set whenever it is. */
  automation_agent_id?: string;
  notifications: Record<string, unknown>[];
  field_mappings?: Record<string, string>;
  opsitem_strategy: OpsItemStrategyType;
  created_at: string;
  updated_at: string;
}

/**
 * Lightweight template list item
 */
export interface RuleTemplateListItem {
  template_id: string;
  owner: TemplateOwnerType;
  customer_id?: string;
  name: string;
  description: string;
  category: string;
  tags: string[];
  rule_type: string;
  trigger_type: string;
  created_at: string;
}

/**
 * A single HTTP auth header sent with a webhook delivery (e.g. Authorization).
 */
export interface WebhookHeader {
  key: string;
  value: string;
}

/**
 * Frontend-only webhook notification target. The backend NotificationTarget does
 * not yet support `channel: 'webhook'` (it's a strict email|sns literal), so a
 * rule carrying one cannot be saved — the editor blocks save until backend
 * delivery lands. Kept out of the generated types deliberately.
 */
export interface WebhookNotificationTarget {
  channel: 'webhook';
  /** Destination URL the event POSTs to */
  url: string;
  /** Optional auth/custom headers; some endpoints require them */
  headers: WebhookHeader[];
  /** JSONPath fallback mappings for the POST body */
  field_mappings: Record<string, string>;
}

/**
 * Notification target as the editor models it: the backend email/sns channels
 * plus the frontend-only webhook channel. Webhook targets are stripped before
 * the save request and block save until the backend supports them.
 */
export type EditorNotificationTarget = NotificationTarget | WebhookNotificationTarget;

export function isWebhookTarget(t: EditorNotificationTarget): t is WebhookNotificationTarget {
  return t.channel === 'webhook';
}

/**
 * Extended rule create request with schedule support (per-channel C-02 shape).
 *
 * Carries the rule-level enrichment prompt and nested per-channel delivery
 * configs (`itsm`, `notification_targets`, `automation`). Mirrors the backend
 * `RuleCreate` exactly — see C-02.
 */
export interface RuleCreateExtended {
  customer_id: string;
  rule_type: RuleTypeType;
  name: string;
  description?: string;
  /** Trigger type - defaults to 'event' for backward compatibility */
  trigger_type: TriggerType;
  /** Event pattern (required if trigger_type is 'event') */
  event_pattern?: Record<string, unknown>;
  /** Schedule expression (required if trigger_type is 'schedule') */
  schedule_expression?: string;
  /** Schedule preset for UI convenience */
  schedule_preset?: SchedulePreset;
  /** Rule-level enrichment prompt (source-aware understanding) */
  enrichment_prompt_id?: string | null;
  /** Run AI enrichment on the event; when false, delivery uses the raw event (D12) */
  summarize?: boolean;
  /** ITSM (Freshservice ticket) delivery channel */
  itsm?: ItsmChannel | null;
  /** Per-channel notification targets (email, sns) */
  notification_targets?: NotificationTarget[];
  /** Automation actor (agent prompt) */
  automation?: AutomationChannelRequest | null;
  /** Recovery signal: auto-resolve the incident when a matching event arrives */
  resolve_signal?: ResolveSignal | null;
  enabled?: boolean;
  event_examples?: Record<string, unknown>[];
  opsitem_strategy?: OpsItemStrategyType;
  /** JSONPath fields composing the dedup fingerprint (fingerprint strategy only) */
  dedup_field_paths?: string[];
}

/**
 * Extended event rule with schedule support (per-channel C-02 shape).
 */
export interface EventRuleExtended {
  rule_id: string;
  rule_type: RuleTypeType;
  name: string;
  description: string;
  /** Trigger type */
  trigger_type: TriggerType;
  /** Event pattern (if event-based) */
  event_pattern?: Record<string, unknown>;
  /** Schedule expression (if schedule-based) */
  schedule_expression?: string;
  /** Schedule preset (if schedule-based) */
  schedule_preset?: SchedulePreset;
  /** Rule-level enrichment prompt */
  enrichment_prompt_id?: string | null;
  /** Whether AI enrichment runs on the event (D12) */
  summarize?: boolean;
  /** ITSM (Freshservice ticket) delivery channel */
  itsm?: ItsmChannel | null;
  /** Per-channel notification targets (email, sns) */
  notification_targets?: NotificationTarget[];
  /** Automation actor (agent prompt) */
  automation?: AutomationChannelRequest | null;
  /** Recovery signal: auto-resolve the incident when a matching event arrives */
  resolve_signal?: ResolveSignal | null;
  status: 'ENABLED' | 'DISABLED';
  created_at: string;
  /** Desired lifecycle status */
  desired_status?: 'active' | 'deleted';
  /** Deployment status per location */
  deployments?: RuleDeployment[];
  /** Aggregate sync status */
  sync_status?: string;
  event_examples?: Record<string, unknown>[];
  opsitem_strategy?: OpsItemStrategyType;
  /** JSONPath fields composing the dedup fingerprint (fingerprint strategy only) */
  dedup_field_paths?: string[];
}

/**
 * Extended rule list item with trigger type and match count
 */
export interface EventRuleListItemExtended {
  rule_id: string;
  rule_type: RuleTypeType;
  name: string;
  description: string;
  has_itsm: boolean;
  has_automation: boolean;
  status: 'ENABLED' | 'DISABLED';
  created_at: string;
  /** Trigger type */
  trigger_type: TriggerType;
  /** Schedule info for display (if schedule-based) */
  schedule_preset?: SchedulePreset;
  /** Desired lifecycle status */
  desired_status?: 'active' | 'deleted';
  /** Aggregate sync status */
  sync_status?: string;
  /** OpsItem strategy */
  opsitem_strategy?: OpsItemStrategyType;
}

/**
 * Event management view type
 */
export type EventManagementView = 'events' | 'rules' | 'templates';

// ============================================================================
// Event Trace Types
// ============================================================================

export type TraceStatus = 'confirmed' | 'failed' | 'inferred' | 'degraded' | 'unmatched' | 'no_data';

export interface TraceStage {
  stage: string;
  label: string;
  account: string;
  account_label: string;
  region: string;
  timestamp?: string;
  delta_ms?: number;
  status: TraceStatus;
  evidence: string;
}

export interface EventTrace {
  trace_id: string;
  event_id: string;
  origin_event: import('./backend').LiveEvent;
  stages: TraceStage[];
  overall_status: TraceStatus;
  path: string;
  xray_trace_id?: string;
  data_sources?: Record<string, boolean>;
}

// ============================================================================
// Async Execution / Operations Dashboard Types
// ============================================================================

export interface AsyncDomainSummary {
  domain_type: string;
  total: number;
  converged: number;
  failed: number;
  in_progress: number;
  pending: number;
  settling: number;
}

export interface ReconcilerStatus {
  domain_type: string;
  enabled: boolean;
  schedule_expression: string;
}

export interface AsyncResourceStatus {
  domain_type: string;
  resource_id: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'CONVERGED' | 'FAILED' | 'DRIFTED';
  execution_id?: string;
  started_at?: string;
  converged_at?: string;
  failed_at?: string;
  drifted_at?: string;
  updated_at: string;
  error?: string;
  cause?: string;
}

export interface AsyncResourceState {
  domain_type: string;
  resource_id: string;
  desired: { data: Record<string, unknown>; version?: number; updated_at: string } | null;
  actual: { data: Record<string, unknown>; updated_at: string } | null;
  status: AsyncResourceStatus | null;
}

export interface AsyncExecution {
  execution_arn: string;
  name: string;
  status: string;
  start_date: string;
  stop_date?: string;
}

export interface AsyncExecutionDetail {
  execution_arn: string;
  status: string;
  input: Record<string, unknown>;
  output: Record<string, unknown> | null;
  start_date: string;
  stop_date?: string;
  error?: string;
  cause?: string;
}

// ============================================================================
// Reconciler Execution Pipeline Types
// ============================================================================

export type ReconcilerActionStatus = 'done' | 'failed' | 'pending';
export type ReconcilerExecutionStatus = 'CONVERGED' | 'DRIFTED' | 'FAILED' | 'IDLE' | 'IN_PROGRESS' | 'PENDING' | 'SETTLING';
export type ReconcilerVerifyOutcome = VerifyOutcomeType;

export interface DriftObservation {
  category: string;
  subject: string;
  summary: string;
  expected?: string | null;
  actual?: string | null;
}

export interface ReconcilerAction {
  display_name: string;
  description: string;
  action_type: string;
  resource_id: string;
  status: ReconcilerActionStatus;
  duration_ms?: number;
  error?: string;
  error_detail?: string;
}

export interface ReconcilerActionGroup {
  label?: string;
  actions: ReconcilerAction[];
  duration_ms?: number;
}

export interface ReconcilerPlan {
  duration_ms: number;
  no_change: boolean;
  summary: string;
  reason?: string | null;
  groups: ReconcilerActionGroup[];
}

export interface ReconcilerApply {
  duration_ms: number;
  error?: string | null;
  groups: ReconcilerActionGroup[];
}

export interface ReconcilerVerify {
  duration_ms: number;
  outcome: ReconcilerVerifyOutcome;
  summary: string;
  observations: DriftObservation[];
}

export interface ReconcilerPlumbingStep {
  display_name: string;
  duration_ms: number;
  status: 'done' | 'failed';
}

export interface ReconcilerExecution {
  id: string;
  resource: string;
  resource_label: string;
  reconciler: string;
  reconciler_description: string;
  domain: string;
  domain_label: string;
  status: ReconcilerExecutionStatus;
  started_at: string;
  total_ms: number;
  plan: ReconcilerPlan | null;
  execute: ReconcilerApply | null;
  verify: ReconcilerVerify | null;
  plumbing: ReconcilerPlumbingStep[];
  // Real per-phase status from the execution history (authoritative, present
  // even for failed runs). Keyed by phase name; the stepper renders these.
  phaseOutcomes: Partial<Record<'observe' | 'plan' | 'execute' | 'verify', PhaseOutcomeType>>;
  error?: string | null;
  cause?: string | null;
  // True when Step Functions no longer has this run (state machine replaced or
  // history expired); `error` and `cause` then carry a notice, not a failure.
  historyUnavailable?: boolean;
}

// Response of customers.MyAccess — the current user's escalation-aware operating
// level on a specific customer, resolved by the backend (single source of truth).
export interface MyAccessResponse {
  customer_id: string | null;
  email: string;
  effective_level: string | null;
  assigned_level: string | null;
  active_escalation: { level: string; expires_at: string | null } | null;
}
